/**
 * useStatusData Hook
 *
 * Manages status diagnostic data lifecycle:
 * - Fetches on mount (for system status banner)
 * - Full refresh when status tab is active
 * - Auto-refresh every 30s when active
 * - Retry with exponential backoff on failure
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchStatusData, StatusData, SystemStatus } from '@/services/statusDataService'
import { isPageVisible } from '@/lib/pageVisibility'

const REFRESH_INTERVAL = 30_000 // 30 seconds
const RETRY_DELAYS = [2000, 4000, 8000, 16000]
const MAX_RETRIES = 4

export interface UseStatusDataResult {
  data: StatusData | null
  loading: boolean
  error: string | null
  refresh: () => void
  systemStatus: SystemStatus
  statusLoading: boolean
}

export function useStatusData(active: boolean): UseStatusDataResult {
  const [data, setData] = useState<StatusData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const retryCountRef = useRef(0)
  const fetchingRef = useRef(false)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const refreshTimerRef = useRef<ReturnType<typeof setInterval>>()

  const doFetch = useCallback(async () => {
    if (fetchingRef.current) return
    fetchingRef.current = true
    setLoading(true)

    try {
      const result = await fetchStatusData()
      setData(result)
      setError(null)
      retryCountRef.current = 0
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('[StatusData] Fetch failed:', msg)
      setError(msg)
      // Schedule retry if under limit
      const next = retryCountRef.current + 1
      retryCountRef.current = next
      if (next <= MAX_RETRIES) {
        const delay = RETRY_DELAYS[Math.min(next - 1, RETRY_DELAYS.length - 1)]
        retryTimerRef.current = setTimeout(() => {
          fetchingRef.current = false
          doFetch()
        }, delay)
      }
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [])

  const refresh = useCallback(() => {
    retryCountRef.current = 0
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    fetchingRef.current = false
    doFetch()
  }, [doFetch])

  // Fetch on mount for system status banner
  useEffect(() => {
    doFetch()
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    }
  }, [doFetch])

  // Auto-refresh when tab is active
  useEffect(() => {
    if (active) {
      refreshTimerRef.current = setInterval(() => {
        if (!isPageVisible()) return
        doFetch()
      }, REFRESH_INTERVAL)
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current)
    }
  }, [active, doFetch])

  const systemStatus: SystemStatus = data?.systemStatus ?? 'loading'
  const statusLoading = !data && loading

  return { data, loading, error, refresh, systemStatus, statusLoading }
}
